// client/src/components/pages/LexiStar/Dashboard/kpiModel.ts
//
// View models for the semantic-layer API (ST-00188 M4-D; m4-contracts §4;
// shapes in types/lexistar-analytics.ts, M4-A):
//   GET /lexistar/metrics                  → MetricsCatalogueResponse
//   GET /lexistar/kpis?…&compare=previous  → KpisResponse
//   GET /lexistar/kpis/breakdown?metric&by[&scope_type&scope_id][&series=true] → BreakdownResponse
//   GET /lexistar/kpis/top-exceptions?date → TopExceptionsResponse
// The screens format these values; they never derive a KPI themselves
// (m4-contracts §1). Every value carries `drill: {route}` — the list that
// explains it (FR-RPT-006).

import type {
  BreakdownResponse,
  BreakdownSeriesPoint,
  KpisResponse,
  MetricDefinition,
  MetricsCatalogueResponse,
  MetricValue as ApiMetricValue,
  TopExceptionKind,
  TopExceptionsResponse,
} from '../../../../types/lexistar-analytics';
import type { PeriodState, SparkPoint } from './analyticsAtoms';
import type { Better, Unit } from './metricFormat';

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// ── Catalogue ───────────────────────────────────────────────────────────────

/** The catalogue entries the screens read (a MetricDefinition, keyed). */
export type MetricDef = Pick<
  MetricDefinition,
  'key' | 'name' | 'description' | 'formula' | 'unit' | 'kind' | 'scopes' | 'grains' | 'provenance_rule' | 'version' | 'tags'
>;

/** Active metrics the caller may read (the API already filtered by permission). */
export function normaliseCatalogue(body: MetricsCatalogueResponse | null | undefined): Map<string, MetricDef> {
  const out = new Map<string, MetricDef>();
  (body?.metrics || []).forEach((m) => {
    if (!m || !m.key || m.active === false) return;
    out.set(m.key, m);
  });
  return out;
}

// ── Drill links ─────────────────────────────────────────────────────────────

/**
 * The client route for a drill link. The metric pack points a few figures at
 * screens that do not exist yet; those are sent to the nearest page that
 * explains them (anything unknown would fall through to the app's catch-all
 * and land on the home page):
 *   /lexistar/routes/:id       → the routes & lanes editor on that route
 *   /lexistar/tickets?…        → the trips of the window (net mass per trip)
 *   /lexistar/cost-ledger?…    → the completed trips of the window (cost per trip on each)
 */
export function resolveDrill(route: string | null | undefined): string | null {
  if (!route) return null;
  const [path = '', query = ''] = route.split('?');
  const m = /^\/lexistar\/routes\/([^/]+)$/.exec(path);
  if (m) return `/settings/modules/monitoring/lexistar-routes?route=${m[1]}`;
  if (path === '/lexistar/tickets') return `/lexistar/trips${query ? `?${query}` : ''}`;
  if (path === '/lexistar/cost-ledger') {
    const q = new URLSearchParams(query);
    if (!q.get('stage')) q.set('stage', 'COMPLETED,RECONCILED,CLOSED');
    return `/lexistar/trips?${q.toString()}`;
  }
  return route;
}

// ── Values ──────────────────────────────────────────────────────────────────

export interface MetricValue {
  value: number | null;
  unit: Unit | null;
  provenance: string | null;
  n: number | null;
  drill: string | null;
}

export function normaliseValue(v: ApiMetricValue | null | undefined): MetricValue {
  return {
    value: num(v?.value),
    unit: v?.unit ?? null,
    provenance: v?.provenance ?? null,
    n: num(v?.n),
    drill: resolveDrill(v?.drill?.route),
  };
}

const valueMap = (v: Record<string, ApiMetricValue> | null | undefined): Record<string, MetricValue> => {
  const out: Record<string, MetricValue> = {};
  Object.entries(v || {}).forEach(([k, val]) => {
    out[k] = normaliseValue(val);
  });
  return out;
};

export interface SeriesPoint {
  period_start: string;
  state: PeriodState;
  values: Record<string, MetricValue>;
}

export interface KpiResult {
  series: SeriesPoint[];
  totals: Record<string, MetricValue>;
  previous: Record<string, MetricValue>;
  previousPeriod: { from: string; to: string } | null;
  definitions_version: string | null;
  /** 'final' only when every point of the window is final. */
  state: PeriodState | null;
  provisionalPeriods: number;
}

export function normaliseKpis(body: KpisResponse | null | undefined): KpiResult {
  const series = (body?.series || [])
    .map((p) => ({
      period_start: String(p.period_start || '').slice(0, 10),
      state: p.state === 'final' ? ('final' as const) : ('provisional' as const),
      values: valueMap(p.values),
    }))
    .filter((p) => p.period_start)
    .sort((a, c) => (a.period_start < c.period_start ? -1 : 1));
  const provisionalPeriods = series.filter((p) => p.state === 'provisional').length;
  return {
    series,
    totals: valueMap(body?.totals),
    previous: valueMap(body?.previous?.totals),
    previousPeriod: body?.previous ? { from: body.previous.from, to: body.previous.to } : null,
    definitions_version: body?.definitions_version ?? null,
    state: body?.state ?? (series.length ? (provisionalPeriods ? 'provisional' : 'final') : null),
    provisionalPeriods,
  };
}

/** One metric's series as sparkline points. */
export const seriesOf = (k: KpiResult, key: string) =>
  k.series.map((p) => ({ value: p.values[key]?.value ?? null, state: p.state, label: p.period_start }));

// ── Breakdown ───────────────────────────────────────────────────────────────

export interface BreakdownRow {
  id: string;
  label: string;
  value: number | null;
  unit: Unit | null;
  provenance: string | null;
  n: number | null;
  share: number | null;
  drill: string | null;
  state: PeriodState | null;
  /** The row's trend (`series=true`) as sparkline points; null when the API sent none. */
  series: SparkPoint[] | null;
}

export interface Breakdown {
  rows: BreakdownRow[];
  unit: Unit | null;
  state: PeriodState | null;
  /** The API answered `series=true` (rows carry their trend): no per-row trend requests. */
  hasSeries: boolean;
}

/** A breakdown row's `series` (one point per `series_grain` bucket) in period order. */
export function seriesPoints(points: BreakdownSeriesPoint[]): SparkPoint[] {
  return points
    .map((p) => ({ value: num(p?.value), label: String(p?.period_start || '').slice(0, 10) }))
    .filter((p) => p.label)
    .sort((a, c) => (a.label < c.label ? -1 : 1));
}

export function normaliseBreakdown(body: BreakdownResponse | null | undefined): Breakdown {
  const rows = (body?.rows || []).map((r) => {
    const v = normaliseValue(r.value);
    return {
      id: String(r.scope_id),
      label: r.label || String(r.scope_id),
      value: v.value,
      unit: v.unit ?? body?.metric?.unit ?? null,
      provenance: v.provenance,
      n: v.n,
      share: num(r.share),
      drill: resolveDrill(r.drill?.route) ?? v.drill,
      state: r.state ?? null,
      series: Array.isArray(r.series) ? seriesPoints(r.series) : null,
    };
  });
  return {
    rows,
    unit: body?.metric?.unit ?? null,
    state: body?.state ?? null,
    hasSeries: rows.some((r) => r.series !== null),
  };
}

// ── Top exceptions ──────────────────────────────────────────────────────────

export type ExceptionKind = TopExceptionKind | 'other';

export interface TopException {
  id: string;
  rank: number;
  kind: ExceptionKind;
  title: string;
  detail: string | null;
  /** The figure behind it, formatted ("38 L", "2.4 km") — `impact` is only the ranking score. */
  figure: string | null;
  severity: string | null;
  drill: string | null;
  /** "TR-260924-0142 · JX12KLGP" */
  subject: string | null;
}

const KINDS: TopExceptionKind[] = [
  'deviation',
  'fuel_overrun',
  'mass_variance',
  'ticket_without_presence',
  'review_item',
  'dwell_exceeded',
];

export function normaliseExceptions(
  body: TopExceptionsResponse | null | undefined,
  format: (v: number, unit: string) => string
): TopException[] {
  return (body?.items || [])
    .map((r, i) => {
      const kind: ExceptionKind = KINDS.includes(r.kind) ? r.kind : 'other';
      const value = num(r.value);
      const subject = [r.trip_reference, r.asset].filter(Boolean).join(' · ') || null;
      return {
        id: r.id || `${kind}-${i}`,
        rank: num(r.rank) ?? i + 1,
        kind,
        title: r.title || 'Exception',
        detail: r.detail || null,
        figure: value !== null ? format(value, r.unit ?? 'count') : null,
        severity: r.severity ?? null,
        drill: resolveDrill(r.drill?.route),
        subject,
      };
    })
    .sort((a, b) => a.rank - b.rank);
}

// ── Headline tiles (LS-03 FR-RPT-001) ───────────────────────────────────────

export interface TileContext {
  keys: string[];
  template: (formatted: string) => string;
}

export interface TileSpec {
  /** Semantic-layer keys (M4-A's metric pack), first one in the catalogue wins. */
  keys: string[];
  label: string;
  better: Better;
  /** Second figures printed under the headline ("of 412 planned · 9 late"). */
  context?: TileContext[];
}

/**
 * The management dashboard's headline set, in reading order (FR-RPT-001:
 * trips planned / completed / late; tonnes moved against plan; on-route
 * compliance; deviations approved / unapproved; fuel budget vs actual in L and
 * R; idle hours and cost; empty running; utilisation and availability; cycle
 * time; cost). A tile whose metric the caller may not read — or that is not in
 * the catalogue — is left out rather than shown empty.
 */
export const HEADLINE_TILES: TileSpec[] = [
  {
    keys: ['trip.completed'],
    label: 'Trips completed',
    better: 'up',
    context: [
      { keys: ['trip.planned'], template: (f) => `of ${f} planned` },
      { keys: ['trip.late'], template: (f) => `${f} late` },
    ],
  },
  {
    keys: ['trip.tonnes_moved', 'trip.tonnes_received'],
    label: 'Tonnes moved',
    better: 'up',
    context: [{ keys: ['trip.tonnes_planned'], template: (f) => `against ${f} planned` }],
  },
  {
    keys: ['route.on_route_compliance_pct'],
    label: 'On-route compliance',
    better: 'up',
    context: [{ keys: ['route.off_route_km'], template: (f) => `${f} off route` }],
  },
  {
    keys: ['route.deviations_unapproved'],
    label: 'Unapproved deviations',
    better: 'down',
    context: [{ keys: ['route.deviations_approved'], template: (f) => `${f} approved` }],
  },
  {
    keys: ['trip.fuel_variance_l'],
    label: 'Fuel against budget',
    better: 'down',
    context: [{ keys: ['fuel.variance_zar'], template: (f) => `${f} est.` }],
  },
  {
    keys: ['asset.idle_hours'],
    label: 'Idle hours',
    better: 'down',
    context: [
      { keys: ['asset.idle_hours_elsewhere'], template: (f) => `${f} outside permitted zones` },
      { keys: ['asset.idle_cost_zar'], template: (f) => `${f} est.` },
    ],
  },
  {
    keys: ['run.empty_running_pct'],
    label: 'Empty running',
    better: 'down',
    context: [{ keys: ['run.km_empty'], template: (f) => `${f} empty` }],
  },
  {
    keys: ['asset.utilisation_pct'],
    label: 'Truck utilisation',
    better: 'up',
    context: [{ keys: ['asset.availability_pct'], template: (f) => `${f} available` }],
  },
  {
    keys: ['lane.avg_cycle_min'],
    label: 'Average cycle time',
    better: 'down',
  },
  {
    keys: ['cost.per_t_zar'],
    label: 'Cost per ton',
    better: 'down',
    context: [{ keys: ['cost.per_tkm_zar'], template: (f) => `${f} per t·km, est.` }],
  },
];

/** Whether a metric is kept for a scope (MetricDefinition.scopes; the whole operation always). */
export const metricKeptFor = (def: Pick<MetricDef, 'scopes'>, scope: string): boolean =>
  scope === 'tenant' || def.scopes.length === 0 || (def.scopes as string[]).includes(scope);

export const resolveKey = (keys: string[], catalogue: Map<string, MetricDef>): string | null =>
  keys.find((k) => catalogue.has(k)) ?? null;

export interface ResolvedContext {
  key: string;
  def: MetricDef;
  template: (formatted: string) => string;
}

export interface ResolvedTile {
  spec: TileSpec;
  key: string;
  def: MetricDef;
  context: ResolvedContext[];
}

export function resolveTiles(catalogue: Map<string, MetricDef>, specs: TileSpec[] = HEADLINE_TILES): ResolvedTile[] {
  const out: ResolvedTile[] = [];
  specs.forEach((spec) => {
    const key = resolveKey(spec.keys, catalogue);
    const def = key ? catalogue.get(key) : undefined;
    if (!key || !def) return;
    const context: ResolvedContext[] = [];
    (spec.context || []).forEach((c) => {
      const k = resolveKey(c.keys, catalogue);
      const d = k ? catalogue.get(k) : undefined;
      if (k && d) context.push({ key: k, def: d, template: c.template });
    });
    out.push({ spec, key, def, context });
  });
  return out;
}

/** The metric keys a set of tiles asks the KPI API for. */
export const tileMetricKeys = (tiles: ResolvedTile[]): string[] => [
  ...new Set(tiles.flatMap((t) => [t.key, ...t.context.map((c) => c.key)])),
];

/** "of 412 planned · 9 late" from the context values that have data. */
export function contextLine(
  t: ResolvedTile,
  totals: Record<string, MetricValue> | undefined,
  format: (v: number, unit: string) => string
): string | null {
  if (!totals) return null;
  const parts = t.context
    .map((c) => {
      const v = totals[c.key];
      return v && v.value !== null ? c.template(format(v.value, v.unit ?? c.def.unit)) : null;
    })
    .filter((x): x is string => Boolean(x));
  return parts.length ? parts.join(' · ') : null;
}

// ── Breakdown metrics offered on the dashboard ──────────────────────────────

export const BREAKDOWN_METRICS: TileSpec[] = [
  HEADLINE_TILES[1] as TileSpec, // tonnes
  HEADLINE_TILES[0] as TileSpec, // trips
  HEADLINE_TILES[2] as TileSpec, // on-route
  HEADLINE_TILES[4] as TileSpec, // fuel variance
  HEADLINE_TILES[5] as TileSpec, // idle
  HEADLINE_TILES[9] as TileSpec, // cost per t
];

// ── Scope (whole operation, or one route / customer) ────────────────────────

export type ScopeType = 'tenant' | 'route' | 'customer' | 'lane' | 'site' | 'asset' | 'driver' | 'material' | 'run';

export interface Scope {
  type: ScopeType;
  id: string | null;
  label?: string;
}

export const TENANT_SCOPE: Scope = { type: 'tenant', id: null };

/** ?scope=route:<id> → Scope; anything else → the whole operation. */
export function readScope(params: URLSearchParams): Scope {
  const raw = params.get('scope');
  if (!raw) return TENANT_SCOPE;
  const i = raw.indexOf(':');
  if (i <= 0) return TENANT_SCOPE;
  const type = raw.slice(0, i) as ScopeType;
  const id = raw.slice(i + 1);
  if (!id || !['route', 'customer', 'lane', 'site', 'asset', 'driver', 'material'].includes(type)) return TENANT_SCOPE;
  return { type, id };
}

export const scopeParam = (s: Scope): string | null => (s.type === 'tenant' || !s.id ? null : `${s.type}:${s.id}`);
